import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { useEffect, useState } from "react";

import { useLogout, useMe, useRegisterPlanIntent, useRepositories } from "../api/queries";
import type { Repository } from "../api/types.gen";
import { Icon } from "../components/Icons";

const DASHBOARD_NAV = [
  { id: "performance", label: "Performance" },
  { id: "tokens", label: "Tokens" },
  { id: "cost", label: "Cost" },
  { id: "time", label: "Time" },
  { id: "code", label: "Code" },
  { id: "trajectory", label: "Agent trajectory" },
] as const;

type DashboardNavID = "overview" | (typeof DASHBOARD_NAV)[number]["id"];

function isDashboardReportPath(pathname: string): boolean {
  return pathname === "/" || /^\/repositories\/[^/]+$/.test(pathname);
}

/**
 * The authenticated application frame.
 *
 * It is also the app's authorization boundary: everything nested under it
 * renders only once /api/v1/me has returned a user. Doing the check here, once,
 * rather than in each screen is what keeps a future screen from forgetting it —
 * the same reasoning as store.WorkspaceScope on the server, applied to routing.
 *
 * This is a UX boundary only. It hides screens; it does not protect data. Every
 * API endpoint authorizes independently, because anything else would be a
 * frontend check standing in for a backend one.
 */
export function Shell() {
  const { data, isPending, isUnauthenticated, isError, error } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardReport = isDashboardReportPath(location.pathname);
  const [activeSection, setActiveSection] = useDashboardNavigation(
    location.pathname,
    data !== undefined,
    dashboardReport,
  );
  const workspace = data?.workspaces[0];
  const { data: repositoryData } = useRepositories(workspace?.slug);

  useEffect(() => {
    if (!isUnauthenticated) return;
    // Carry where they were trying to go, so sign-in returns them there
    // instead of dumping everyone on the root.
    const returnTo = location.pathname + location.search;
    navigate(`/login?return_to=${encodeURIComponent(returnTo)}`, { replace: true });
  }, [isUnauthenticated, location, navigate]);

  if (isPending || isUnauthenticated) {
    return (
      <div className="app-boot" role="status">
        Loading your workspace…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="app-boot app-boot-error" role="alert">
        <h1>BERBench is unavailable</h1>
        <p>{error instanceof Error ? error.message : "Unknown error."}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  const repositories = repositoryData?.repositories ?? [];
  const addingRepository = location.pathname === "/repositories/new";
  const routeRepositoryID = location.pathname.match(/^\/repositories\/([^/]+)/)?.[1];
  const selectedRepository = addingRepository
    ? undefined
    : repositories.find((repository) => repository.id === routeRepositoryID) ??
      repositories[0];

  return (
    <div className="app-shell top-shell">
      <main className="main">
        <header className="dashboard-header">
          <Link className="wordmark" to="/" aria-label="BERBench benchmarks">
            <span className="production-wordmark" aria-hidden="true">
              berbench<span>_</span>
            </span>
          </Link>

          <nav className="product-nav" aria-label="Primary navigation">
            <a
              href={dashboardReport ? "#view" : "/"}
              className={activeSection === "overview" ? "active" : undefined}
              aria-current={activeSection === "overview" ? "location" : undefined}
              onClick={() => setActiveSection("overview")}
            >
              Overview
            </a>
            {DASHBOARD_NAV.map((item) => (
              <a
                key={item.id}
                href={dashboardReport ? `#${item.id}` : `/#${item.id}`}
                className={activeSection === item.id ? "active" : undefined}
                aria-current={activeSection === item.id ? "location" : undefined}
                onClick={() => setActiveSection(item.id)}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="header-actions">
            <UpgradeButton slug={workspace?.slug} />
            <RepositoryMenu
              repositories={repositories}
              selected={selectedRepository}
              adding={addingRepository}
              onSelect={(repository) => navigate(`/repositories/${repository.id}`)}
            />
            <AccountMenu
              displayName={data.user.display_name}
              email={data.user.email}
            />
          </div>
        </header>

        <section id="view">
          <Outlet />
        </section>
      </main>
    </div>
  );
}

/**
 * Keeps the single dashboard navigation in sync with the report while it
 * scrolls. The report arrives after the shell's first render, so a mutation
 * observer schedules the same inexpensive position check when its sections
 * are inserted; scroll and resize events cover everything after that.
 */
function useDashboardNavigation(
  pathname: string,
  shellReady: boolean,
  dashboardReport: boolean,
) {
  const [active, setActive] = useState<DashboardNavID | null>(
    dashboardReport ? "overview" : null,
  );

  useEffect(() => {
    if (!dashboardReport) {
      setActive(null);
      return;
    }

    let frame = 0;

    const update = () => {
      frame = 0;
      const sections = DASHBOARD_NAV
        .map((item) => ({ id: item.id, element: document.getElementById(item.id) }))
        .filter(
          (item): item is { id: (typeof DASHBOARD_NAV)[number]["id"]; element: HTMLElement } =>
            item.element !== null,
        );

      if (sections.length === 0) {
        setActive("overview");
        return;
      }

      // At the document bottom, select the final visible section even when it
      // is too short for its heading to cross the normal activation line.
      const atBottom =
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setActive(sections[sections.length - 1].id);
        return;
      }

      const activationLine = 64 + Math.min(window.innerHeight * 0.28, 240);
      let next: DashboardNavID = "overview";
      for (const section of sections) {
        if (section.element.getBoundingClientRect().top <= activationLine) {
          next = section.id;
        }
      }
      setActive(next);
    };

    const schedule = () => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const view = document.getElementById("view");
    const observer = new MutationObserver(schedule);
    if (view !== null) observer.observe(view, { childList: true, subtree: true });
    schedule();

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [pathname, shellReady, dashboardReport]);

  return [active, setActive] as const;
}

/**
 * The header's Upgrade button.
 *
 * `.premium-button` has been in src/styles since the design was lifted, with
 * nothing rendering it. This is the markup it was waiting for.
 *
 * The intent is recorded and the navigation happens in the same handler,
 * without awaiting: a failed POST loses one data point, and a navigation
 * blocked behind a request loses the user. `header_button` is its own source
 * because a click here is a click made WITHOUT the price on screen — a
 * different thing from the one on /plans, and mixing the two into one count
 * would answer a question nobody asked.
 */
function UpgradeButton({ slug }: { slug?: string }) {
  const register = useRegisterPlanIntent(slug);
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className="premium-button"
      onClick={() => {
        register.mutate({ plan: "pro", source: "header_button" });
        navigate("/plans");
      }}
    >
      Upgrade
    </button>
  );
}

function RepositoryMenu({
  repositories,
  selected,
  adding,
  onSelect,
}: {
  repositories: Repository[];
  selected?: Repository;
  adding: boolean;
  onSelect: (repository: Repository) => void;
}) {
  const label = adding
    ? "Add repository"
    : selected
      ? repositoryName(selected)
      : "No repositories";

  return (
    <details className="repo-menu">
      <summary className="repo-switcher" aria-label="Select repository">
        <span className="repo-avatar">{label.slice(0, 1).toUpperCase()}</span>
        <span>{label}</span>
        <Icon name="chevron-down" />
      </summary>
      <div className="repo-popover" role="menu">
        <span className="menu-label">Repositories</span>
        {repositories.map((repository, index) => (
          <button
            key={repository.id}
            type="button"
            role="menuitem"
            className={repository.id === selected?.id ? "selected" : undefined}
            onClick={(event) => {
              onSelect(repository);
              event.currentTarget.closest("details")?.removeAttribute("open");
            }}
          >
            <span className={`repo-avatar ${["tone-blue", "tone-green", "tone-amber"][index % 3]}`}>
              {repositoryName(repository).slice(0, 1).toUpperCase()}
            </span>
            <span>
              <strong>{repositoryName(repository)}</strong>
              <small>{repository.id === selected?.id ? "Current benchmark" : "View benchmark"}</small>
            </span>
            {repository.id === selected?.id && <Icon name="check" className="check" />}
          </button>
        ))}
        <Link
          className={adding ? "repo-add selected" : "repo-add"}
          to="/repositories/new"
          role="menuitem"
          onClick={(event) => {
            event.currentTarget.closest("details")?.removeAttribute("open");
          }}
        >
          <span className="repo-add-icon" aria-hidden="true">+</span>
          <span>
            <strong>Add new repository</strong>
            <small>Set up with berbench init</small>
          </span>
          {adding ? <Icon name="check" className="check" /> : <Icon name="arrow" />}
        </Link>
      </div>
    </details>
  );
}

function AccountMenu({
  displayName,
  email,
}: {
  displayName: string;
  email: string;
}) {
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <details className="account-menu">
      <summary aria-label="Open account menu">
        <span className="user-avatar">{initials(displayName)}</span>
      </summary>
      <div>
        <span>
          <strong>{displayName}</strong>
          <small>{email}</small>
        </span>
        <button type="button" onClick={() => window.open("https://benchdocs.ber.run", "_blank", "noopener,noreferrer")}>
          Documentation
        </button>
        <button
          type="button"
          disabled={logout.isPending}
          onClick={() => {
            // Navigate on settle, not on success: if the request failed we do
            // not know whether the session survived, and leaving the user in a
            // signed-in-looking app is the worse guess.
            logout.mutate(undefined, {
              onSettled: () => navigate("/login", { replace: true }),
            });
          }}
        >
          {logout.isPending ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </details>
  );
}

function repositoryName(repository: Repository): string {
  if (repository.owner !== "" && repository.name !== "") {
    return `${repository.owner} / ${repository.name}`;
  }
  return repository.name || repository.repo_id;
}

/** Two-letter avatar text, matching the prototype's treatment. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
